import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import HeaderBar from '../../components/ui/HeaderBar';
import RoleBasedSidebar from '../../components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';

// Import components for each tab
import BrandIdentityTab from './components/BrandIdentityTab';
import ColorPaletteTab from './components/ColorPaletteTab';
import TypographyTab from './components/TypographyTab';
import ComponentLibraryTab from './components/ComponentLibraryTab';
import LogoManagementTab from './components/LogoManagementTab';
import ThemePreviewPanel from './components/ThemePreviewPanel';
import ExportPanel from './components/ExportPanel';

const DesignSystemThemeConfiguration = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('brand-identity');
  const [themeConfig, setThemeConfig] = useState({
    brandIdentity: {
      logoUrl: '/assets/images/image-1750685807825.png',
      companyName: 'CLAB',
      tagline: 'Construction & Engineering Biodata Management',
      brandColors: {
        primary: '#2D9B9B',
        secondary: '#1A7A7A',
        accent: '#3DB8B8'
      }
    },
    colorPalette: {
      primary: '#2D9B9B',
      primaryLight: '#4DAEAE',
      primaryDark: '#1A7A7A',
      secondary: '#1A7A7A',
      accent: '#3DB8B8',
      success: '#059669',
      warning: '#d97706',
      error: '#dc2626',
      neutral: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a'
      }
    },
    typography: {
      fontFamily: {
        heading: 'Inter',
        body: 'Inter',
        mono: 'JetBrains Mono'
      },
      fontSizes: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
        '4xl': '2.25rem'
      }
    },
    components: {
      buttons: {
        borderRadius: '0.5rem',
        padding: '0.75rem 1.5rem'
      },
      cards: {
        borderRadius: '0.75rem',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      },
      forms: {
        borderRadius: '0.375rem',
        borderColor: '#e2e8f0'
      }
    }
  });

  const tabs = [
    { id: 'brand-identity', label: 'Brand Identity', icon: 'Palette' },
    { id: 'color-palette', label: 'Color Palette', icon: 'Droplets' },
    { id: 'typography', label: 'Typography', icon: 'Type' },
    { id: 'component-library', label: 'Component Library', icon: 'Package' },
    { id: 'logo-management', label: 'Logo Management', icon: 'Image' }
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/master-admin-dashboard' },
    { label: 'System Configuration', href: '#' },
    { label: 'Design System & Theme Configuration', href: '/design-system-theme-configuration', current: true }
  ];

  const handleThemeUpdate = (section, updates) => {
    setThemeConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...updates
      }
    }));
  };

  const handleExportTheme = () => {
    const cssVariables = generateCSSVariables(themeConfig);
    const designTokens = generateDesignTokens(themeConfig);
    
    // Create downloadable files
    const cssBlob = new Blob([cssVariables], { type: 'text/css' });
    const tokensBlob = new Blob([JSON.stringify(designTokens, null, 2)], { type: 'application/json' });
    
    // Download CSS file
    const cssUrl = URL.createObjectURL(cssBlob);
    const cssLink = document.createElement('a');
    cssLink.href = cssUrl;
    cssLink.download = 'clab-theme-variables.css';
    cssLink.click();
    
    // Download tokens file
    const tokensUrl = URL.createObjectURL(tokensBlob);
    const tokensLink = document.createElement('a');
    tokensLink.href = tokensUrl;
    tokensLink.download = 'clab-design-tokens.json';
    tokensLink.click();
    
    URL.revokeObjectURL(cssUrl);
    URL.revokeObjectURL(tokensUrl);
  };

  const generateCSSVariables = (config) => {
    return `
/* CLAB Design System CSS Variables */
:root {
  /* Brand Colors */
  --clab-primary: ${config.colorPalette.primary};
  --clab-primary-light: ${config.colorPalette.primaryLight};
  --clab-primary-dark: ${config.colorPalette.primaryDark};
  --clab-secondary: ${config.colorPalette.secondary};
  --clab-accent: ${config.colorPalette.accent};
  
  /* Status Colors */
  --clab-success: ${config.colorPalette.success};
  --clab-warning: ${config.colorPalette.warning};
  --clab-error: ${config.colorPalette.error};
  
  /* Typography */
  --clab-font-heading: ${config.typography.fontFamily.heading};
  --clab-font-body: ${config.typography.fontFamily.body};
  --clab-font-mono: ${config.typography.fontFamily.mono};
  
  /* Component Styles */
  --clab-button-radius: ${config.components.buttons.borderRadius};
  --clab-card-radius: ${config.components.cards.borderRadius};
  --clab-form-radius: ${config.components.forms.borderRadius};
}
    `.trim();
  };

  const generateDesignTokens = (config) => {
    return {
      brand: {
        name: config.brandIdentity.companyName,
        tagline: config.brandIdentity.tagline,
        logo: config.brandIdentity.logoUrl
      },
      colors: config.colorPalette,
      typography: config.typography,
      components: config.components,
      version: '1.0.0',
      timestamp: new Date().toISOString()
    };
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'brand-identity':
        return (
          <BrandIdentityTab
            config={themeConfig.brandIdentity}
            onUpdate={(updates) => handleThemeUpdate('brandIdentity', updates)}
          />
        );
      case 'color-palette':
        return (
          <ColorPaletteTab
            config={themeConfig.colorPalette}
            onUpdate={(updates) => handleThemeUpdate('colorPalette', updates)}
          />
        );
      case 'typography':
        return (
          <TypographyTab
            config={themeConfig.typography}
            onUpdate={(updates) => handleThemeUpdate('typography', updates)}
          />
        );
      case 'component-library':
        return (
          <ComponentLibraryTab
            config={themeConfig}
          />
        );
      case 'logo-management':
        return (
          <LogoManagementTab
            config={themeConfig.brandIdentity}
            onUpdate={(updates) => handleThemeUpdate('brandIdentity', updates)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole={user?.role || 'master_admin'}
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        userName={user?.full_name || 'Master Admin'}
      />
      
      <RoleBasedSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        userRole={user?.role || 'master_admin'}
      />

      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'} pt-16`}>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <BreadcrumbNavigation items={breadcrumbItems} />
            <div className="mt-4 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Design System & Theme Configuration</h1>
                <p className="mt-2 text-text-secondary">
                  Configure the visual identity and branding for the eBiodata CLAB platform
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleExportTheme}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] text-white rounded-lg hover:from-[#1A7A7A] hover:to-[#2D9B9B] transition-all duration-200"
                >
                  <Icon name="Download" size={16} />
                  <span>Export Theme</span>
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Main Configuration Area */}
            <div className="xl:col-span-3">
              <div className="bg-surface rounded-xl shadow-lg border border-secondary-200">
                {/* Tab Navigation */}
                <div className="border-b border-secondary-200 bg-secondary-50 rounded-t-xl">
                  <nav className="flex space-x-1 p-4 overflow-x-auto">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] text-white shadow-md'
                            : 'text-text-secondary hover:bg-secondary-100 hover:text-text-primary'
                        }`}
                      >
                        <Icon name={tab.icon} size={16} />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {renderActiveTab()}
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="xl:col-span-1">
              <div className="sticky top-24">
                <ThemePreviewPanel config={themeConfig} />
                <div className="mt-6">
                  <ExportPanel onExport={handleExportTheme} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DesignSystemThemeConfiguration;